/**
 * @file The Brewery Tour's page, apart from booking (assets/tourBooking.js): Read all reviews and
 * Write a review (sections/tour-reviews.liquid), and Show all questions (tour-faq). Without this
 * script Junip's widget shows every review, and every question shows.
 */

/* ----------------------------------------------------------------------- */
/*  DOM elements                                                           */
/* ----------------------------------------------------------------------- */

const SEL = {
	allReviews: '[data-tour-all-reviews]',
	readReviews: '[data-tour-reviews-open]',
	writeReview: '[data-tour-reviews-write]',
	junipWrite: '.junip-write-review-cta',
	faq: '[data-tour-faq]',
	faqMore: '[data-tour-faq-more]',
	faqAll: '[data-tour-faq-all]',
};

/** How long Write a review waits for Junip's widget to arrive, in ms. */
const JUNIP_WAIT = 10000;

/* ----------------------------------------------------------------------- */
/*  Reviews                                                                */
/* ----------------------------------------------------------------------- */

/**
 * Every review stays closed under the reviews until Read all reviews opens it. Write a review opens
 * it too, then Junip's own form once Junip's widget has arrived.
 *
 * @param {HTMLElement} region
 * @param {ParentNode} scope - Where its buttons are.
 */
function initReviews(region, scope) {
	const read = scope.querySelector(SEL.readReviews);
	const write = scope.querySelector(SEL.writeReview);
	region.hidden = true;
	read?.setAttribute('aria-expanded', 'false');

	const open = () => {
		region.hidden = false;
		read?.setAttribute('aria-expanded', 'true');
		region.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	read?.addEventListener('click', (event) => {
		event.preventDefault();
		open();
	});

	write?.addEventListener('click', (event) => {
		event.preventDefault();
		open();
		const found = region.querySelector(SEL.junipWrite);
		if (found) {
			found.click();
			return;
		}
		// Junip draws its widget once it's in view, so wait for its button.
		const observer = new MutationObserver(() => {
			const button = region.querySelector(SEL.junipWrite);
			if (button) {
				observer.disconnect();
				button.click();
			}
		});
		observer.observe(region, { childList: true, subtree: true });
		setTimeout(() => observer.disconnect(), JUNIP_WAIT);
	});
}

/* ----------------------------------------------------------------------- */
/*  Questions                                                              */
/* ----------------------------------------------------------------------- */

/**
 * The first few questions show; Show all opens out the rest and moves to the first of them.
 *
 * @param {HTMLElement} list
 */
function initQuestions(list) {
	const more = Array.from(list.querySelectorAll(SEL.faqMore));
	const button = list.querySelector(SEL.faqAll);
	if (more.length === 0 || !button) {
		return;
	}
	more.forEach((question) => {
		question.hidden = true;
	});
	button.hidden = false;
	button.addEventListener('click', () => {
		more.forEach((question) => {
			question.hidden = false;
		});
		button.remove();
		more[0].querySelector('summary')?.focus();
	});
}

/* ----------------------------------------------------------------------- */
/*  Public API                                                             */
/* ----------------------------------------------------------------------- */

/**
 * Start the page's reviews and questions, or those in a section the theme editor has just drawn
 * again.
 *
 * @param {ParentNode} [scope]
 */
export function initTourPage(scope = document) {
	const reviews = scope.querySelector(SEL.allReviews);
	if (reviews) {
		initReviews(reviews, scope);
	}
	scope.querySelectorAll(SEL.faq).forEach(initQuestions);
}

initTourPage();
document.addEventListener('shopify:section:load', (event) => initTourPage(event.target));
